import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { BgWrapper } from 'views/Home/components/Hero'
import styled from 'styled-components'
import { RouteComponentProps } from 'react-router-dom'
import { Flex, useMatchBreakpoints, CardBody, ArrowDownIcon, Button, Text, useModal, Card } from 'vvs-uikit'
import { Currency, CurrencyAmount, Fraction, Token, Pair, ETHER, JSBI, Percent, currencyEquals, WETH } from 'vvs-sdk'
import { TransactionResponse } from '@ethersproject/providers'
import { usePollFarmsWithUserData } from 'state/farms/hooks'
import CompositeImage, { CompositeImageProps } from 'views/Home/components/CompositeImage'
import FlexLayout from 'components/Layout/Flex'
import { AppBody, AppHeader } from 'components/App'
import CurrencyInputPanel from 'components/CurrencyInputPanel'
import { useTranslation } from 'contexts/Localization'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import { useCurrency } from 'hooks/Tokens'
import { tryParseAmount } from 'state/swap/hooks'
import { useCurrencyBalance } from 'state/wallet/hooks'
import { useGasPrice, useUserSlippageTolerance, useExpertModeManager } from 'state/user/hooks'
import { useFarmPairs } from 'hooks/Pair'
import ConnectWalletButton from 'components/ConnectWalletButton'
import { ApprovalState, useApproveCallback } from 'hooks/useApproveCallback'
import { ZAP_ADDRESS } from 'config/constants'
import CircleLoader from 'components/Loader/CircleLoader'
import { MinimalPositionCard } from 'components/PositionCard'
import {
  useZapState,
  useZapActionHandlers,
  useTrades,
  fetchEstimateZapInToLpSwapPaths,
  fetchEstimateAddLiquidityOutputAmount,
} from 'state/zap/hooks'
import { getTokenAddress } from 'views/Swap/components/Chart/utils'
import BigNumber from 'bignumber.js'
import useDebounce from 'hooks/useDebounce'
import TransactionConfirmationModal, { ConfirmationModalContent } from 'components/TransactionConfirmationModal'
import { DoubleCurrencyLogo } from 'components/Logo'
import { useTransactionAdder } from 'state/transactions/hooks'
import { calculateGasMargin, getZapContract } from 'utils'
import { BIG_ZERO } from 'utils/bigNumber'
import { BigNumber as BigNumberHexable } from '@ethersproject/bignumber'
import useToast from 'hooks/useToast'
import useTotalSupply from 'hooks/useTotalSupply'
import useTheme from 'hooks/useTheme'
import ExpandableSectionButton from 'components/ExpandableSectionButton'
import confirmPriceImpactWithoutFee from 'views/Swap/components/confirmPriceImpactWithoutFee'

import Column, { AutoColumn, ColumnCenter } from '../../components/Layout/Column'
import Row, { AutoRow, RowBetween, RowFixed } from '../../components/Layout/Row'
import { currencyId } from '../../utils/currencyId'
import { maxAmountSpend } from '../../utils/maxAmountSpend'
import ProgressSteps from '../ProgressSteps'
import SwapDetail from './SwapDetail'

const FlashLpWrapper = styled(Flex)`
  background: ${({ theme }) => (theme.isDark ? theme.colors.secondary : theme.colors.backgroundAlt)};
  position: relative;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  padding: 60px 16px 75px 16px;
  z-index: -1;
  min-height: 100vh;

  ${({ theme }) => theme.mediaQueries.md} {
    padding-bottom: 125px;
  }
`

const BackgroundWrapper = styled(BgWrapper)``

const FlashLpBody = styled(AppBody)`
  padding: 0px;
  background-color: ${({ theme }) => theme.colors.backgroundAlt};
  border-radius: 18px;
  align-self: center;
`

const PercentageButton = styled(Button)`
  border-radius: 50px;
  width: 50px;
  height: 36px;
  padding: 9px 12px;
  margin: 0px 6px;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
`

const Tab = styled(Button)<{ active: boolean }>`
  padding: 1px;
  margin-right: 24px;
  color: ${({ active, theme }) => (active ? theme.colors.primary : theme.colors.text)};
  border-bottom: ${({ active }) => (active ? 'solid 3px' : 'none')};
  border-radius: 0;
  &:hover {
    border-bottom: solid 3px;
    color: ${({ theme }) => theme.colors.primary};
  }
`
const FixedHeightRow = styled(RowBetween)`
  height: 24px;
`
const BreakRow = styled(RowBetween)`
  height: 12px;
`

const ExpandingWrapper = styled.div`
  padding: 24px 0;
  border-top: 2px solid ${({ theme }) => theme.colors.cardBorder};
  overflow: hidden;
  background-color: ${({ theme }) => (theme.isDark ? theme.colors.background : theme.colors.beige)});
`

const bgImages: CompositeImageProps = {
  path: '/images/zap/',
  attributes: [
    {
      src: 'gems1',
      alt: 'gems1',
      coordination: {
        left: '5%',
        top: '10%',
        height: '387px',
        width: '387px',
      },
    },
    {
      src: 'gems2',
      alt: 'gems2',
      coordination: {
        right: '5%',
        top: '10%',
        height: '387px',
        width: '387px',
      },
    },
    {
      src: 'gems3',
      alt: 'gems3',
      coordination: {
        right: '10%',
        top: '50%',
        height: '160px',
        width: '160px',
      },
    },
    {
      src: 'diamondCoin',
      alt: 'diamondCoin',
      coordination: {
        left: '10%',
        bottom: '5%',
        height: '400px',
        width: '338px',
      },
    },
    {
      src: 'mole',
      alt: 'mole',
      coordination: {
        right: '18%',
        bottom: '0%',
        height: '300px',
        width: '220px',
      },
    },
  ],
}

export default function FlashLp({
  match: {
    params: { currencyIdA, lpPairIdB },
  },
  history,
}: RouteComponentProps<{ currencyIdA?: string; lpPairIdB?: string }>) {
  usePollFarmsWithUserData(true)
  const { isDesktop } = useMatchBreakpoints()
  const { t } = useTranslation()
  const { toastError } = useToast()
  const [activeIndex, setActiveIndex] = useState(1)
  const { account, chainId, library } = useActiveWeb3React()
  // get custom setting values for user
  const [allowedSlippage] = useUserSlippageTolerance()

  // for expert mode
  const [isExpertMode] = useExpertModeManager()

  const allFarmPairs = useFarmPairs()
  const currency = useCurrency(currencyIdA)
  const tokenAddress = getTokenAddress(currencyIdA)
  const pair: Pair = useMemo(() => {
    const pairArr = lpPairIdB && allFarmPairs.filter((farmPair) => farmPair?.liquidityToken.address === lpPairIdB)
    return pairArr && pairArr.length !== 0 ? pairArr[0] : null
  }, [lpPairIdB, allFarmPairs])
  const pairAddress = pair?.liquidityToken.address
  const token0Currency = useCurrency(pair?.token0?.address)
  const token1Currency = useCurrency(pair?.token1?.address)
  const pairToken0Decimals = pair?.token0?.decimals
  const pairToken1Decimals = pair?.token1?.decimals
  const totalSupply = useTotalSupply(pair?.liquidityToken)

  const oneCurrencyIsWETH = Boolean(chainId && currency && currencyEquals(currency, WETH[chainId]))

  const { onUserInput } = useZapActionHandlers()
  const { typedValue } = useZapState()
  const debouncedTypedValue = useDebounce(typedValue, 400)
  const [estimateLpAmount, setEstimateLpAmount] = useState(BIG_ZERO)
  const formattedEstimateLpAmount = useMemo(() => {
    return estimateLpAmount.multipliedBy(new BigNumber(10).exponentiatedBy(-18))
  }, [estimateLpAmount])
  const { theme } = useTheme()

  // modal and loading
  const [attemptingTxn, setAttemptingTxn] = useState<boolean>(false) // clicked confirm
  const [txHash, setTxHash] = useState<string>('')
  // const [zapErrorMessage, setZapErrorMessage] = useState(undefined)

  const parsedAmount: CurrencyAmount | undefined = useMemo(
    () => tryParseAmount(debouncedTypedValue, currency),
    [debouncedTypedValue, currency],
  )
  const selectedCurrencyBalance = useCurrencyBalance(account ?? undefined, currency ?? undefined)
  const maxAmountInput: CurrencyAmount | undefined = maxAmountSpend(selectedCurrencyBalance)

  let inputError: string | undefined
  const [routeError, setRouteError] = useState<string | undefined>('')
  if (!account) {
    inputError = t('Connect Wallet')
  }
  if (!currency) {
    inputError = inputError ?? t('Select a token')
  }
  if (!parsedAmount) {
    inputError = inputError ?? t('Enter an amount')
  }
  if (!pair) {
    inputError = inputError ?? t('Select a LP Pair')
  }
  if (parsedAmount && selectedCurrencyBalance?.lessThan(parsedAmount)) {
    inputError = t('Insufficient %symbol% balance', { symbol: currency.symbol })
  }
  if (routeError !== '') {
    inputError = routeError
  }
  const isValid = !inputError && !routeError

  // check whether the user has approved the zap contract on the input token
  const [approval, approveCallback] = useApproveCallback(parsedAmount, ZAP_ADDRESS)
  const [approvalSubmitted, setApprovalSubmitted] = useState<boolean>(false)
  useEffect(() => {
    if (approval === ApprovalState.PENDING) {
      setApprovalSubmitted(true)
    }
  }, [approval, approvalSubmitted])

  const [{ pathBeforeLP, amountsBeforeLP, token0OutputLp, token1OutputLp }, setEstimateState] = useState<{
    pathBeforeLP: string[]
    amountsBeforeLP: string[]
    token0OutputLp: string
    token1OutputLp: string
  }>({
    pathBeforeLP: undefined,
    amountsBeforeLP: undefined,
    token0OutputLp: undefined,
    token1OutputLp: undefined,
  })

  const { steps, stepsAmounts, swapTrades, priceImpactSeverity, currPriceImpactWithoutFee } = useTrades({
    amount: parsedAmount,
    currCurrency: currency,
    swapPathBeforeLP: pathBeforeLP,
    swapAmountsBeforeLP: amountsBeforeLP,
    pair,
  })

  // show approve flow when: no error on inputs, not approved or pending, or approved in current session
  const showApproveFlow =
    !inputError &&
    (approval === ApprovalState.NOT_APPROVED ||
      approval === ApprovalState.PENDING ||
      (approvalSubmitted && approval === ApprovalState.APPROVED)) &&
    !(priceImpactSeverity > 3 && !isExpertMode)

  useEffect(() => {
    const fetchEstimateLpAmount = async () => {
      if (tokenAddress && pairAddress && parsedAmount && pairToken0Decimals && pairToken1Decimals) {
        const res = await fetchEstimateZapInToLpSwapPaths(
          tokenAddress,
          `0x${parsedAmount.raw.toString(16)}`,
          pairAddress,
        )
        if (res) {
          const { swapPathBeforeLP, swapAmountsBeforeLP, amountForToken0, amountForToken1 } = res
          const token0BigNumber = new BigNumber(amountForToken0._hex).multipliedBy(
            new BigNumber(10).exponentiatedBy(-pairToken0Decimals),
          )
          const token1BigNumber = new BigNumber(amountForToken1._hex).multipliedBy(
            new BigNumber(10).exponentiatedBy(-pairToken1Decimals),
          )
          setEstimateState({
            pathBeforeLP: swapPathBeforeLP,
            amountsBeforeLP: swapAmountsBeforeLP,
            token0OutputLp: token0BigNumber.toString(),
            token1OutputLp: token1BigNumber.toString(),
          })
          const amount = await fetchEstimateAddLiquidityOutputAmount(amountForToken0, amountForToken1, pairAddress)
          setEstimateLpAmount(new BigNumber(amount._hex))
          if (
            amountForToken0.toString() === '0' ||
            amountForToken1.toString() === '0' ||
            new BigNumber(amount._hex).toString() === '0'
          ) {
            setRouteError(t('Input amount too low'))
          }
        } else {
          setRouteError(t('Insufficient liquidity for this trade.'))
        }
      }
    }
    fetchEstimateLpAmount()
  }, [tokenAddress, pairAddress, parsedAmount, setRouteError, t, pairToken0Decimals, pairToken1Decimals])

  const handleChangePercent = useCallback(
    (sliderPercent: number) => {
      if (maxAmountInput) {
        setEstimateLpAmount(BIG_ZERO) // Reset when typing new values
        setRouteError('')
        const percent = new Fraction(sliderPercent.toString(), '100')
        const decimals =
          sliderPercent === 100 ? maxAmountInput.currency.decimals : Math.min(maxAmountInput.currency.decimals, 6)
        onUserInput(maxAmountInput.multiply(percent).toFixed(decimals))
      }
    },
    [maxAmountInput, onUserInput],
  )

  useEffect(() => {
    onUserInput('') // reset input value during new page load
  }, [onUserInput])

  const handleTypeInput = (value: string) => {
    setApprovalSubmitted(false) // reset 2 step UI for approvals
    setEstimateLpAmount(BIG_ZERO) // Reset when typing new values
    setRouteError('')
    onUserInput(value)
  }

  const handleCurrencyASelect = useCallback(
    (currencyA: Currency) => {
      setApprovalSubmitted(false) // reset 2 step UI for approvals
      setEstimateLpAmount(BIG_ZERO) // Reset when typing new values
      if (routeError !== '') setRouteError('')
      const newCurrencyIdA = currencyId(currencyA)
      if (lpPairIdB) {
        history.push(`/zap/${newCurrencyIdA}/${lpPairIdB}`)
        return
      }
      history.push(`/zap/${newCurrencyIdA}`)
    },
    [history, lpPairIdB, routeError],
  )
  const handleLPPairBSelect = useCallback(
    (lpPairB: Pair) => {
      setEstimateLpAmount(BIG_ZERO) // Reset when typing new values
      history.push(`/zap/${currencyIdA || 'CRO'}/${lpPairB.liquidityToken.address}`)
    },
    [currencyIdA, history],
  )

  // text to show while loading
  const pendingText = t('Flash LP: %amountA% %symbolA% to ~%estimateLpAmount% %lpPairB% LP', {
    amountA: parsedAmount?.toSignificant(6) ?? '',
    symbolA: currency?.symbol ?? '',
    estimateLpAmount: formattedEstimateLpAmount?.precision(6).toString() ?? '',
    lpPairB: `${pair?.token0.symbol}-${pair?.token1.symbol}`,
  })

  const gasPrice = useGasPrice()
  const addTransaction = useTransactionAdder()

  const onFlashSupply = async () => {
    if (!chainId || !library || !account) return
    if (currPriceImpactWithoutFee && !confirmPriceImpactWithoutFee(currPriceImpactWithoutFee, t)) {
      return
    }
    const vvsZap = getZapContract(chainId, library, account)
    const outputAmountMin = JSBI.divide(
      JSBI.multiply(JSBI.BigInt(estimateLpAmount), JSBI.BigInt(10000 - allowedSlippage)),
      JSBI.BigInt(10000),
    )
    let estimate
    let method: (...args: any) => Promise<TransactionResponse>
    let args: Array<string | string[] | number>
    let value: BigNumberHexable | null | string
    if (currency === ETHER) {
      estimate = vvsZap.estimateGas.zapIn
      method = vvsZap.zapIn
      args = [
        pairAddress, // lpAddress
        outputAmountMin.toString(),
      ]
      value = BigNumberHexable.from(parsedAmount.raw.toString())
    } else {
      estimate = vvsZap.estimateGas.zapInToken
      method = vvsZap.zapInToken
      args = [
        tokenAddress, // fromToken
        `0x${parsedAmount.raw.toString(16)}`, // inputAmount
        pairAddress, // lpAddress
        outputAmountMin.toString(),
      ]
      value = null
    }
    setAttemptingTxn(true)
    await estimate(...args, value ? { value } : {})
      .then((estimatedGasLimit) =>
        method(...args, {
          ...(value ? { value } : {}),
          gasLimit: calculateGasMargin(estimatedGasLimit),
          gasPrice,
        }).then((response) => {
          setAttemptingTxn(false)
          let summary = ''
          steps.forEach(({ action, symbol }, index) => {
            summary +=
              action === 'deposit'
                ? `${index + 1}) Deposited ${stepsAmounts[index]} ${symbol}|`
                : `${index + 1}) Swapped ${stepsAmounts[index]} ${symbol}|`
          })
          summary += `${steps.length + 1}) Supplied tokens for ${pair?.token0.symbol}-${pair?.token1.symbol} LP`
          setTxHash(response.hash)
          setEstimateLpAmount(BIG_ZERO)
          onUserInput('')
          addTransaction(response, {
            summary,
            // summary: `Converted ${parsedAmount?.toSignificant(6)} ${
            //   currency?.symbol
            // } to ${formattedEstimateLpAmount.toString()} ${pair?.token0.symbol}-${pair?.token1.symbol} LP tokens`,
            isZap: true,
          })
        }),
      )
      .catch((err) => {
        setAttemptingTxn(false)
        // we only care if the error is something _other_ than the user rejected the tx
        if (err?.code !== 4001) {
          console.error(err)
        }
        toastError(
          t('Transaction incompleted'),
          t('Unable to complete the Flash LP transaction. Please decrease the input amount by 1-3% and retry.'),
        )
      })
  }

  const ModalHeader = () => {
    return (
      <AutoColumn>
        <Text fontSize="13px">You will receive</Text>
        <Flex alignItems="center">
          <Text fontSize="28px" marginRight="10px">
            ~
            {estimateLpAmount.isZero()
              ? parsedAmount
                ? 'Estimating ...'
                : '-'
              : formattedEstimateLpAmount?.precision(6).toString()}
          </Text>
          <DoubleCurrencyLogo currency0={pair.token0} currency1={pair.token1} size={30} />
        </Flex>
        <Row>
          <Text fontSize="20px">{`${pair?.token0.symbol}/${pair?.token1.symbol} Pool Tokens`}</Text>
        </Row>
        <Text small textAlign="left" my="24px" color="textSubtle">
          {t('Output is estimated. If the price changes by more than %slippage%% your transaction will revert.', {
            slippage: allowedSlippage / 100,
          })}
        </Text>
      </AutoColumn>
    )
  }

  const ModalBottom = () => {
    const [showExpandableSection, setShowExpandableSection] = useState(false)
    return (
      <>
        <Button variant={priceImpactSeverity > 2 ? 'danger' : 'primary'} onClick={onFlashSupply} mt="20px" width="100%">
          {t('Confirm Supply')}
        </Button>
        <ExpandingWrapper>
          <ExpandableSectionButton
            onClick={() => setShowExpandableSection(!showExpandableSection)}
            expanded={showExpandableSection}
          />
          {showExpandableSection && (
            <>
              <FixedHeightRow />
              {steps.map(({ action, percentage, symbol }, index) => (
                <FixedHeightRow key={symbol}>
                  <RowFixed>
                    <Text small color="textSubtle">
                      {`${index + 1}) ${action === 'deposit' ? `${symbol} Deposited` : `Swap ${percentage} ${symbol}`}`}
                    </Text>
                  </RowFixed>
                  <RowFixed>
                    <Text small color="textSubtle">
                      {stepsAmounts[index]}
                    </Text>
                  </RowFixed>
                </FixedHeightRow>
              ))}
              <BreakRow />
              <FixedHeightRow>
                <RowFixed>
                  <Text small color="textSubtle">
                    Rates
                  </Text>
                </RowFixed>
                <RowFixed>
                  <Text small color="textSubtle">
                    {`1 ${pair?.token0.symbol} = ${pair?.priceOf(pair?.token0).toSignificant(6)} ${
                      pair?.token1.symbol
                    } `}
                  </Text>
                </RowFixed>
              </FixedHeightRow>
              <FixedHeightRow>
                <RowFixed>
                  <Text />
                </RowFixed>
                <RowFixed>
                  <Text small color="textSubtle">
                    {`1 ${pair?.token1.symbol} = ${pair?.priceOf(pair?.token1).toSignificant(6)} ${
                      pair?.token0.symbol
                    } `}
                  </Text>
                </RowFixed>
              </FixedHeightRow>
              <FixedHeightRow>
                <RowFixed>
                  <Text small color="textSubtle">
                    Share of Pool
                  </Text>
                </RowFixed>
                <RowFixed>
                  <Text small color="textSubtle">
                    {!estimateLpAmount.isZero() && totalSupply
                      ? new Percent(
                          JSBI.BigInt(estimateLpAmount),
                          JSBI.add(JSBI.BigInt(totalSupply.raw), JSBI.BigInt(estimateLpAmount)),
                        )?.toSignificant(4)
                      : parsedAmount
                      ? 'Estimating ...'
                      : '-'}
                    %
                  </Text>
                </RowFixed>
              </FixedHeightRow>
            </>
          )}
        </ExpandingWrapper>
      </>
    )
  }

  const handleConfirmDismiss = useCallback(() => {
    // if there was a tx hash, we want to clear the input
    if (txHash) {
      onUserInput('')
    }
    setTxHash('')
  }, [onUserInput, setTxHash, txHash])

  const [onPresentFlashSupplyModal] = useModal(
    <TransactionConfirmationModal
      title={t('Confirm Supply Liquidity')}
      customOnDismiss={handleConfirmDismiss}
      attemptingTxn={attemptingTxn}
      hash={txHash}
      content={() => <ConfirmationModalContent topContent={ModalHeader} bottomContent={ModalBottom} />}
      pendingText={pendingText}
      currencyToAdd={pair?.liquidityToken}
      callbackOnExit
    />,
    true,
    true,
    'flashSupplyModal',
  )

  const renderContent = () => {
    switch (activeIndex) {
      case 1:
        return (
          <Card background={theme.colors.backgroundAlt2}>
            <CardBody>
              <AutoColumn gap="16px">
                <FixedHeightRow>
                  <RowFixed>
                    <DoubleCurrencyLogo currency0={pair?.token0} currency1={pair?.token1} margin size={20} />
                    <Text small color="textSubtle">
                      {pair?.token0.symbol}-{pair?.token1.symbol} LP
                    </Text>
                  </RowFixed>
                </FixedHeightRow>
                <AutoColumn gap="4px">
                  <FixedHeightRow>
                    <Text color="textSubtle" small>
                      {pair?.token0.symbol} per {pair?.token1.symbol}
                    </Text>
                    <Text>{pair?.priceOf(pair?.token0).toSignificant(6)}</Text>
                  </FixedHeightRow>
                  <FixedHeightRow>
                    <Text color="textSubtle" small>
                      {pair?.token1.symbol} per {pair?.token0.symbol}
                    </Text>
                    <Text>{pair?.priceOf(pair?.token1).toSignificant(6)}</Text>
                  </FixedHeightRow>
                  <FixedHeightRow>
                    <Text color="textSubtle" small>
                      {t('Share of Pool')}:
                    </Text>
                    <Text>
                      {!estimateLpAmount.isZero() && totalSupply
                        ? new Percent(
                            JSBI.BigInt(estimateLpAmount),
                            JSBI.add(JSBI.BigInt(totalSupply.raw), JSBI.BigInt(estimateLpAmount)),
                          )?.toSignificant(4)
                        : parsedAmount
                        ? 'Estimating ...'
                        : '-'}
                      %
                    </Text>
                  </FixedHeightRow>
                  <BreakRow />
                  <FixedHeightRow>
                    <Text color="textSubtle" small>
                      {pair?.token0.symbol} in output LP
                    </Text>
                    <Text>{tryParseAmount(token0OutputLp, token0Currency)?.toSignificant(6)}</Text>
                  </FixedHeightRow>
                  <FixedHeightRow>
                    <Text color="textSubtle" small>
                      {pair?.token1.symbol} in output LP
                    </Text>
                    <Text>{tryParseAmount(token1OutputLp, token1Currency)?.toSignificant(6)}</Text>
                  </FixedHeightRow>
                </AutoColumn>
              </AutoColumn>
            </CardBody>
          </Card>
        )
      case 2:
        return (
          <Card background={theme.colors.backgroundAlt2}>
            <CardBody>
              {swapTrades.map(({ token0, token1, route, price, priceImpact, liquidityProviderFee }) => (
                <SwapDetail
                  key={`${route}-${price}`}
                  token0={token0}
                  token1={token1}
                  route={route}
                  price={price}
                  priceImpact={priceImpact}
                  liquidityProviderFee={liquidityProviderFee}
                />
              ))}
            </CardBody>
          </Card>
        )
      default:
        break
    }
    return null
  }

  return (
    <FlashLpWrapper>
      <BackgroundWrapper>
        {isDesktop && (
          <Flex>
            <CompositeImage {...bgImages} />
          </Flex>
        )}
      </BackgroundWrapper>
      <FlashLpBody>
        <AppHeader title={t('Flash LP ⚡')} subtitle={t('Form LP tokens and start farming in one step!')} />
        <CardBody>
          <AutoColumn gap="20px">
            <CurrencyInputPanel
              label="From token"
              value={typedValue}
              onUserInput={handleTypeInput}
              onCurrencySelect={handleCurrencyASelect}
              showMaxButton={false}
              currency={currency}
              id="zap-input-tokenA"
            />
            <AutoColumn justify="space-between">
              <AutoRow justify="center" style={{ padding: '0 1rem' }}>
                <PercentageButton onClick={() => handleChangePercent(25)}>25%</PercentageButton>
                <PercentageButton onClick={() => handleChangePercent(50)}>50%</PercentageButton>
                <PercentageButton onClick={() => handleChangePercent(75)}>75%</PercentageButton>
                <PercentageButton onClick={() => handleChangePercent(100)}>MAX</PercentageButton>
              </AutoRow>
            </AutoColumn>
            <ColumnCenter>
              <ArrowDownIcon width="16px" />
            </ColumnCenter>
            <CurrencyInputPanel
              label="To LP (estimate)"
              value={
                estimateLpAmount.isZero()
                  ? parsedAmount && pair && !inputError
                    ? 'Estimating ...'
                    : '-'
                  : formattedEstimateLpAmount?.precision(6).toString()
              }
              onPairSelect={handleLPPairBSelect}
              showMaxButton={false}
              pair={pair}
              id="zap-output-lpPair"
              disabledInput
            />
            {!estimateLpAmount.isZero() && (
              <>
                <Flex>
                  <Tab
                    variant="text"
                    scale="sm"
                    active={activeIndex === 1}
                    onClick={() => {
                      setActiveIndex(1)
                    }}
                  >
                    {t('PRICES & POOL SHARE')}
                  </Tab>
                  <Tab
                    variant="text"
                    scale="sm"
                    active={activeIndex === 2}
                    onClick={() => {
                      setActiveIndex(2)
                    }}
                  >
                    {t('SWAP DETAILS')}
                  </Tab>
                </Flex>
                {renderContent()}
              </>
            )}
            {!account ? (
              <ConnectWalletButton width="100%" scale="lg" />
            ) : showApproveFlow ? (
              <RowBetween>
                <Button
                  variant={approval === ApprovalState.APPROVED ? 'success' : 'primary'}
                  onClick={approveCallback}
                  disabled={approval !== ApprovalState.NOT_APPROVED || approvalSubmitted}
                  width="48%"
                >
                  {approval === ApprovalState.PENDING ? (
                    <AutoRow gap="6px" justify="center">
                      {t('Enabling')} <CircleLoader stroke="white" />
                    </AutoRow>
                  ) : approvalSubmitted && approval === ApprovalState.APPROVED ? (
                    t('Enabled')
                  ) : (
                    t('Enable %asset%', { asset: currency?.symbol ?? '' })
                  )}
                </Button>
                <Button
                  variant={isValid && priceImpactSeverity > 2 ? 'danger' : 'primary'}
                  onClick={() => {
                    onPresentFlashSupplyModal()
                  }}
                  width="48%"
                  id="zap-button"
                  disabled={
                    !isValid ||
                    approval !== ApprovalState.APPROVED ||
                    estimateLpAmount.isZero() ||
                    (priceImpactSeverity > 3 && !isExpertMode)
                  }
                >
                  {priceImpactSeverity > 3 && !isExpertMode ? t('Price Impact High') : t('Flash Supply')}
                </Button>
              </RowBetween>
            ) : (
              <Button
                variant={isValid && priceImpactSeverity > 2 ? 'danger' : 'primary'}
                onClick={() => {
                  onPresentFlashSupplyModal()
                }}
                id="zap-button"
                width="100%"
                scale="lg"
                disabled={
                  !isValid ||
                  approval !== ApprovalState.APPROVED ||
                  estimateLpAmount.isZero() ||
                  (priceImpactSeverity > 3 && !isExpertMode)
                }
              >
                {inputError || (priceImpactSeverity > 3 && !isExpertMode ? t('Price Impact High') : t('Flash Supply'))}
              </Button>
            )}
            {showApproveFlow && (
              <Column style={{ marginTop: '1rem' }}>
                <ProgressSteps steps={[approval === ApprovalState.APPROVED]} />
              </Column>
            )}
          </AutoColumn>
        </CardBody>
      </FlashLpBody>
      <FlexLayout>
        {pair && (
          <AutoColumn style={{ minWidth: '20rem', width: '100%', maxWidth: '570px', marginTop: '1rem' }}>
            <MinimalPositionCard showUnwrapped={oneCurrencyIsWETH} pair={pair} source="zap" />
          </AutoColumn>
        )}
      </FlexLayout>
    </FlashLpWrapper>
  )
}
